import moment from 'moment'
import { YearMonth } from 'core-system/MonthRangePicker/DropdownCalendar'
import { AiReportColumn, HeaderCell, TableDataType } from './types'

const getMoment = (date: YearMonth) => {
  const dateString = moment(
    date.year +
      date.month.toLocaleString('en-US', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      }),
    'YYYYMM'
  )
  return dateString
}

const formatAiReportCell = (value: unknown): string | number | unknown[] => {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }
  if (typeof value === 'number') {
    return value
  }
  if (Array.isArray(value)) {
    return value
  }
  return String(value)
}

const inferAiReportCustomCell = (value: unknown): string | null => {
  if (Array.isArray(value)) {
    return 'List'
  }
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return 'Date'
  }
  return null
}

const mapAiReportToTable = (
  columns: AiReportColumn[],
  rows: Record<string, unknown>[]
): { headerCells: HeaderCell[]; tableData: TableDataType[][] } => {
  const headerCells = columns.map((column, index) => ({
    displayName: column.displayName,
    key: column.key,
    isSearchable: true,
    isSortable: index === 0,
  }))
  const tableData = rows.map((row) =>
    columns.map((column) => {
      const value = row[column.key]
      return {
        text: formatAiReportCell(value),
        customCell: inferAiReportCustomCell(value),
      }
    })
  )
  return { headerCells, tableData }
}

const formatReportCellText = (cell: TableDataType): string => {
  const { text, customCell } = cell
  if (text === null || text === undefined || text === '') {
    return ''
  }
  if (customCell === 'Date') {
    return moment(text as moment.MomentInput).format('MM/DD/YYYY')
  }
  if (customCell === 'Currency') {
    return `$${text}`
  }
  if (customCell === 'Large Number') {
    return Number(text).toFixed(2)
  }
  if (customCell === 'List') {
    return Array.isArray(text) ? text.join(', ') : String(text)
  }
  return String(text)
}

const escapeCsvValue = (value: string): string => {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

const buildTableCsv = (
  headerCells: HeaderCell[],
  rows: TableDataType[][]
): string => {
  const header = headerCells
    .map((column) => escapeCsvValue(column.displayName))
    .join(',')
  const body = rows.map((row) =>
    row.map((cell) => escapeCsvValue(formatReportCellText(cell))).join(',')
  )
  return [header, ...body].join('\n')
}

export {
  getMoment,
  mapAiReportToTable,
  formatReportCellText,
  buildTableCsv,
}
